/* eslint-disable @typescript-eslint/no-explicit-any */

import {
  AlertCircle,
  ArrowLeft,
  Calendar,
  CheckCircle2,
  Clock,
  DollarSign,
  ExternalLink,
  Eye,
  RotateCcw,
  Users,
} from 'lucide-react';
import { useEffect } from 'react';
import { useAnnouncePresence, useWorkspacePresence } from '../../hooks/useWorkspacePresence';
import { isThreadAnalyzed, threadDisplayTitle } from '../../lib/clinic-logic';
import { mailThreadLink } from '../../lib/mail-links';
import { getActiveWorkspaceId, trpc } from '../../lib/trpc';
import { cn } from '../../lib/utils';
import type { EmailThreadMetadata } from '../../types/email';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { UserAvatar } from '../../ui/user-avatar';
import { AnalysisNotice } from './AnalysisNotice';

export interface ThreadDetailHeaderProps {
  thread: EmailThreadMetadata;
  workspaceMembers: Array<{ id: number; email: string; fullName: string | null }>;
  onBack: () => void;
  onToggleResolved?: () => void;
  isTogglingResolved?: boolean;
  /** Re-run analysis for this thread; shown only when the failure is retryable. */
  onRetryAnalysis?: (threadId: string) => void;
  isReadOnly?: boolean;
}

export function ThreadDetailHeader({
  thread,
  workspaceMembers,
  onBack,
  onToggleResolved,
  isTogglingResolved,
  onRetryAnalysis,
  isReadOnly,
}: ThreadDetailHeaderProps) {
  const meQuery = trpc.auth.me.useQuery();
  const currentUserId = meQuery.data?.uid;
  const workspaceId = getActiveWorkspaceId();
  const threadId: string | undefined = thread?.threadId;

  const markViewed = trpc.drafts.markViewed.useMutation();
  const readReceiptsQuery = trpc.drafts.getReadReceipts.useQuery(
    { threadId: threadId || '' },
    { enabled: !!threadId }
  );

  const presence = useWorkspacePresence(workspaceId, currentUserId ?? null);
  useAnnouncePresence(workspaceId, currentUserId ?? null, threadId || null);
  const viewers =
    (threadId ? presence.usersByContext[threadId] : undefined)?.filter(
      (id) => id !== currentUserId
    ) || [];

  useEffect(() => {
    if (threadId) {
      markViewed.mutate({ threadId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId, markViewed.mutate]);

  if (!thread) return null;

  const { url: mailUrl, label: mailLinkLabel } = mailThreadLink(thread);

  return (
    <div className="space-y-4 pb-4 border-b">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" className="h-8 gap-1.5 text-xs -ml-2" onClick={onBack}>
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to Triage
        </Button>
        <div className="flex items-center gap-3">
          {viewers.length > 0 && (
            <div className="flex items-center gap-1.5" title="Currently viewing this thread">
              <Eye className="h-3.5 w-3.5 text-muted-foreground" />
              <div className="flex -space-x-2">
                {viewers.map((userId) => {
                  const member = workspaceMembers.find((m) => m.id === userId);
                  return (
                    <UserAvatar
                      key={userId}
                      user={member}
                      size="xs"
                      className="border-2 border-background"
                    />
                  );
                })}
              </div>
            </div>
          )}
          {onToggleResolved && !isReadOnly && (
            <Button
              variant={thread.resolvedAt ? 'outline' : 'default'}
              size="sm"
              className="h-8 gap-1.5 text-xs"
              onClick={onToggleResolved}
              disabled={isTogglingResolved}
            >
              {thread.resolvedAt ? (
                <>
                  <RotateCcw className="h-3.5 w-3.5" />
                  Reopen
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  Mark Resolved
                </>
              )}
            </Button>
          )}
          <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs" asChild>
            <a href={mailUrl} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="h-3.5 w-3.5 text-foreground" />
              {mailLinkLabel}
            </a>
          </Button>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          {thread.resolvedAt && (
            <Badge variant="secondary" className="bg-muted text-foreground border-none">
              <CheckCircle2 className="h-3 w-3 mr-1" />
              Resolved
            </Badge>
          )}
          {/* Shown only with an analysis behind them — see TriageThreadCard for why
 rendering these unconditionally is worse than omitting them. */}
          {isThreadAnalyzed(thread) ? (
            <>
              <Badge
                variant="secondary"
                className="bg-primary/5 text-primary border border-primary/10"
              >
                {thread.domainCategory}
              </Badge>
              <Badge variant="outline" className="border-border">
                {thread.actionType}
              </Badge>
              <Badge
                variant="secondary"
                className={cn(
                  'border-none',
                  thread.difficultyLevel === 'Hard'
                    ? 'bg-muted text-foreground'
                    : thread.difficultyLevel === 'Moderate'
                      ? 'bg-muted text-foreground'
                      : 'bg-muted text-foreground'
                )}
              >
                {thread.difficultyLevel}
              </Badge>
              {thread.estimatedEffortMinutes !== null && (
                <div className="flex items-center gap-1 text-xs text-muted-foreground ml-1">
                  <Clock className="h-3.5 w-3.5" /> {thread.estimatedEffortMinutes}m effort
                </div>
              )}
            </>
          ) : (
            <AnalysisNotice thread={thread} onRetry={onRetryAnalysis} />
          )}
        </div>
        <h1 className="display font-display text-2xl font-normal tracking-tight leading-snug text-foreground">
          {threadDisplayTitle(thread)}
        </h1>
        {thread.senderEmail && (
          <p className="text-xs text-muted-foreground">
            From:{' '}
            <span className="font-semibold text-foreground">
              {thread.senderName || thread.senderEmail}
            </span>{' '}
            ({thread.senderEmail})
          </p>
        )}
        {(readReceiptsQuery.data?.length || 0) > 0 && (
          <p className="text-[10px] text-muted-foreground">
            Seen by {readReceiptsQuery.data!.map((r) => r.fullName || r.email).join(', ')}
          </p>
        )}
      </div>

      {thread.coreAsk && (
        <div className="bg-primary/5 border border-primary/15 rounded-xl p-4 space-y-2">
          <div className="flex items-center gap-2 text-primary font-semibold text-xs">
            <AlertCircle className="h-4 w-4" /> Core Ask
          </div>
          <p className="text-sm font-medium leading-relaxed text-foreground whitespace-pre-line">
            {thread.coreAsk}
          </p>
        </div>
      )}

      {thread.extractedEntities && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {thread.extractedEntities.keyStakeholders?.length > 0 && (
            <div className="space-y-1.5 bg-muted border rounded-xl p-3">
              <p className="text-[10px] font-semibold text-muted-foreground flex items-center gap-1">
                <Users className="h-3.5 w-3.5 text-muted-foreground" /> Stakeholders
              </p>
              <div className="flex flex-wrap gap-1">
                {thread.extractedEntities.keyStakeholders.map((s: string, i: number) => (
                  <Badge
                    key={i}
                    variant="outline"
                    className="bg-background text-[10px] font-medium border-border"
                  >
                    {s}
                  </Badge>
                ))}
              </div>
            </div>
          )}
          {thread.extractedEntities.deadlines?.length > 0 && (
            <div className="space-y-1.5 bg-muted border rounded-xl p-3">
              <p className="text-[10px] font-semibold text-muted-foreground flex items-center gap-1">
                <Calendar className="h-3.5 w-3.5 text-foreground" /> Deadlines
              </p>
              <div className="flex flex-wrap gap-1">
                {thread.extractedEntities.deadlines.map((d: string, i: number) => (
                  <Badge
                    key={i}
                    variant="outline"
                    className="bg-muted text-foreground text-[10px] font-semibold border-border"
                  >
                    {d}
                  </Badge>
                ))}
              </div>
            </div>
          )}
          {thread.extractedEntities.monetaryValues?.length > 0 && (
            <div className="space-y-1.5 bg-muted border rounded-xl p-3 md:col-span-2">
              <p className="text-[10px] font-semibold text-muted-foreground flex items-center gap-1">
                <DollarSign className="h-3.5 w-3.5 text-foreground" /> Financial Impact
              </p>
              <div className="flex flex-wrap gap-1.5">
                {thread.extractedEntities.monetaryValues.map((v: string, i: number) => (
                  <Badge
                    key={i}
                    variant="outline"
                    className="bg-muted text-foreground text-[10px] font-semibold border-border"
                  >
                    {v}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
